import type { Proposta, PropostaItem } from '@/types'
import type { FinanceProposalSnapshotRow } from '@/services/financeService'
import api from '@/services/api'
import {
  generateAnnexPdf,
  generateAnnexScopePageHTML,
  generateAnnexSummaryPageHTML,
  htmlToCanvas,
  canvasToPdfPage,
  type AnnexContractedItem,
} from '@/utils/pdfExport'
import {
  buildExportPlanDataForItem,
  buildInvestmentLabelFromItem,
  buildLoyaltyLabelFromItem,
  buildPaymentDetailsFromItem,
  type BuildExportPlanDataOptions,
} from '@/utils/proposalExport'
import { calculateAllVariations } from '@/utils/dealCalculations'
import {
  buildInvestmentLabelFromComboVariation,
  buildLoyaltyLabelFromComboVariation,
  buildPaymentDetailsFromComboVariation,
  listOrphanItemsForCombo,
  pickPrimaryComboVariation,
  resolveComboVariationPricing,
} from '@/utils/comboVariationPricing'
import { mapApiPlanToCatalog, overlayCatalogScopeOnItem, type CatalogPlan } from '@/utils/planCatalog'
import { normalizePropostaForExport } from '@/utils/normalizeProposta'
import { parseFinanceProposalSnapshot } from './financeProposalSnapshotParse'
import jsPDF from 'jspdf'

async function loadCatalogPlans(): Promise<CatalogPlan[]> {
  try {
    const { data } = await api.get('/plans')
    const rawPlans = data.data ?? data ?? []
    return rawPlans.map((raw: unknown) =>
      mapApiPlanToCatalog(raw as unknown as Record<string, unknown>),
    )
  } catch {
    return []
  }
}

function enrichItemsWithCatalog(
  items: PropostaItem[],
  catalogPlans: CatalogPlan[],
): PropostaItem[] {
  if (catalogPlans.length === 0) return items
  return items.map((item) => overlayCatalogScopeOnItem(item, catalogPlans))
}

function resolvePaymentText(
  formData: Record<string, unknown>,
  items: PropostaItem[],
): string {
  const cond = formData.condicaoPagamento
  if (typeof cond === 'string' && cond.trim()) return cond.trim()
  if (items.length === 1) return buildPaymentDetailsFromItem(items[0])
  return items.map((item) => buildPaymentDetailsFromItem(item)).filter(Boolean).join(' · ')
}

function buildProposalFromSnapshot(
  snapshot: FinanceProposalSnapshotRow,
  formData: Record<string, unknown>,
  parsedItems: PropostaItem[],
  meta: ReturnType<typeof parseFinanceProposalSnapshot>['meta'],
): Proposta {
  const total =
    Number(formData.valorTotal ?? meta.totalFinal ?? snapshot.totalFinal ?? 0) ||
    parsedItems.reduce((acc, item) => acc + Number(item.total ?? 0), 0)

  return {
    id: snapshot.id,
    dealId: '',
    versao: snapshot.versao ?? 1,
    status: 'aceita',
    itens: parsedItems,
    items: parsedItems,
    totalFinal: total,
    valor: total,
    validade: 0,
    desconto: 0,
    subtotal: total,
    observacoes: meta.observacoes,
    dados: JSON.stringify(meta),
    createdAt: snapshot.createdAt,
    updatedAt: snapshot.createdAt,
  }
}

function buildContractedRows(
  items: PropostaItem[],
  parsed: ReturnType<typeof parseFinanceProposalSnapshot>,
): AnnexContractedItem[] {
  const pricedVariations = calculateAllVariations(parsed.comboVariations, items)
  const primaryCombo = pickPrimaryComboVariation(pricedVariations, items)
  const orphanItems = listOrphanItemsForCombo(items, primaryCombo)

  if (primaryCombo) {
    const rows: AnnexContractedItem[] = [
      {
        nome: primaryCombo.nome,
        loyalty: buildLoyaltyLabelFromComboVariation(primaryCombo),
        payment: buildPaymentDetailsFromComboVariation(primaryCombo),
        investment: buildInvestmentLabelFromComboVariation(primaryCombo),
      },
    ]
    for (const item of orphanItems) {
      rows.push({
        nome: item.descricao || item.nome || 'Serviço',
        loyalty: buildLoyaltyLabelFromItem(item),
        payment: buildPaymentDetailsFromItem(item),
        investment: buildInvestmentLabelFromItem(item),
      })
    }
    return rows
  }

  return items.map((item) => ({
    nome: item.descricao || item.nome || 'Serviço',
    loyalty: buildLoyaltyLabelFromItem(item),
    payment: buildPaymentDetailsFromItem(item),
    investment: buildInvestmentLabelFromItem(item),
  }))
}

function resolveAnnexTotal(
  formData: Record<string, unknown>,
  parsed: ReturnType<typeof parseFinanceProposalSnapshot>,
  snapshot: FinanceProposalSnapshotRow,
  items: PropostaItem[],
): number {
  const pricedVariations = calculateAllVariations(parsed.comboVariations, items)
  const primaryCombo = pickPrimaryComboVariation(pricedVariations, items)
  if (primaryCombo) {
    return resolveComboVariationPricing(primaryCombo).total
  }

  return (
    Number(formData.valorTotal ?? parsed.meta.totalFinal ?? snapshot.totalFinal ?? 0) ||
    items.reduce((acc, item) => acc + Number(item.total ?? 0), 0)
  )
}

export async function generateFinanceClosedScopeAnnexPdf(
  snapshot: FinanceProposalSnapshotRow,
  formData: Record<string, unknown>,
  empresaNome: string,
): Promise<Blob> {
  const parsed = parseFinanceProposalSnapshot(snapshot)
  const catalogPlans = await loadCatalogPlans()
  const items = enrichItemsWithCatalog(parsed.items, catalogPlans)
  if (items.length === 0) {
    throw new Error('Nenhum serviço no escopo para gerar o Anexo 1')
  }

  const proposal = buildProposalFromSnapshot(snapshot, formData, items, parsed.meta)
  const exportOptions: BuildExportPlanDataOptions = {
    dealServico:
      typeof formData.servicosContratados === 'string'
        ? formData.servicosContratados
        : undefined,
    catalogPlans,
  }

  const paymentText = resolvePaymentText(formData, items)
  const contractedItems = buildContractedRows(items, parsed)
  const totalNum = resolveAnnexTotal(formData, parsed, snapshot, items)

  const pricedVariations = calculateAllVariations(parsed.comboVariations, items)
  const primaryCombo = pickPrimaryComboVariation(pricedVariations, items)
  const scopeItems = primaryCombo
    ? [...primaryCombo.items, ...listOrphanItemsForCombo(items, primaryCombo)]
    : items

  const comboPaymentText = primaryCombo
    ? buildPaymentDetailsFromComboVariation(primaryCombo)
    : paymentText

  if (scopeItems.length === 1) {
    const plan = buildExportPlanDataForItem(proposal, scopeItems[0], exportOptions)
    const enrichedPlan = {
      ...plan,
      paymentDetails: comboPaymentText,
      pricing: {
        ...plan.pricing,
        total: totalNum > 0 ? totalNum : plan.pricing.total,
        valor: totalNum > 0 ? totalNum : plan.pricing.valor,
      },
    }
    return generateAnnexPdf(enrichedPlan, empresaNome, contractedItems)
  }

  const normalized = normalizePropostaForExport(proposal)
  const scopePlans = scopeItems.map((item) => buildExportPlanDataForItem(normalized, item, exportOptions))
  const summaryPlan = {
    ...scopePlans[0],
    paymentDetails: comboPaymentText,
    pricing: {
      ...scopePlans[0].pricing,
      total: totalNum > 0 ? totalNum : scopePlans[0].pricing.total,
      valor: totalNum > 0 ? totalNum : scopePlans[0].pricing.valor,
    },
  }

  const totalPages = scopePlans.length + 1
  const pdf = new jsPDF('p', 'mm', 'a4')

  for (let i = 0; i < scopePlans.length; i += 1) {
    const scopeHtml = generateAnnexScopePageHTML(
      scopePlans[i],
      empresaNome,
      `${i + 1}/${totalPages}`,
    )
    const scopeCanvas = await htmlToCanvas(scopeHtml)
    canvasToPdfPage(pdf, scopeCanvas, i > 0)
  }

  const summaryHtml = generateAnnexSummaryPageHTML(
    summaryPlan,
    contractedItems,
    `${totalPages}/${totalPages}`,
  )
  const summaryCanvas = await htmlToCanvas(summaryHtml)
  canvasToPdfPage(pdf, summaryCanvas, true)

  return pdf.output('blob')
}

export function downloadFinanceClosedScopeAnnexPdf(
  blob: Blob,
  empresaNome: string,
  protocol?: string,
): void {
  const safeName = empresaNome.replace(/[^\w\s-]/g, '').trim() || 'cliente'
  const suffix = protocol ? `-${protocol}` : ''
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `anexo-1-${safeName}${suffix}.pdf`
  anchor.click()
  URL.revokeObjectURL(url)
}
